// /procurement/board-returns — the shipments of failed boards, newest first.
//
// Defaults to the WORKLIST (draft and sent) for the same reason the purchase
// returns page does: a list of every manifest ever raised is a report somebody
// opens twice a year, and a list of what is still in play is a thing somebody
// acts on.
//
// The primary action here is starting one, because that is what a tech walking
// over with a tray of dead boards needs, and every other control on this page is
// in service of finding a draft they already started.

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useToast } from '../../components/Toast/ToastContext';
import PageHeader, { PrimaryAction } from '../../components/PageHeader';
import PageLayout from '../../components/ui/PageLayout';
import { Tag } from '../../components/ui/Tag';
import { FilterBar, FilterSelect } from '../../components/FilterBar';
import { EmptyState } from '../../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../../components/ui/Skeleton';
import { DataTable } from '../../components/table/DataTable';
import useUserStore from '../../stores/useUserStore';
import { useBoardReturnsApi, type ManifestRow, type ManifestStatus } from './boardReturnsApi';

const STATUS_META: Record<ManifestStatus, { label: string; tone: 'accent' | 'info' | 'good' | 'neutral' }> = {
  draft: { label: 'Draft', tone: 'accent' },
  sent: { label: 'Sent', tone: 'info' },
  closed: { label: 'Closed', tone: 'good' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Module scope, per the DataTable contract: a fresh columns array hands
// flexRender a new component TYPE and remounts every cell. Nothing here closes
// over page state and no cell calls back into the page, so this table passes no
// `meta` at all.
//
// No sorting: the service hands these back newest first, which is the order a
// tech looking for the draft they started ten minutes ago wants.
//
// No `text-sm` in any cellClass below: the vessel's own table class sets it for
// the whole field, so the six `text-sm` utilities the old <td>s each carried are
// gone. The one on the manifest Link stays because it is inside the cell's
// content, which carries over unchanged.
// ---------------------------------------------------------------------------
const MANIFEST_COLUMNS: ColumnDef<ManifestRow, any>[] = [
  {
    id: 'manifest_number',
    header: 'Manifest',
    // An identifier never wraps, the same rule the PO number column carries.
    meta: { cellClass: 'whitespace-nowrap' },
    // The way in stays a LINK rather than becoming a row click: middle-click,
    // copy-link-address and the keyboard all keep working without this page
    // reimplementing any of them.
    cell: ({ row }) => (
      <Link
        to={`/procurement/board-returns/${row.original.id}`}
        className="font-mono text-sm text-primary hover:underline"
      >
        {row.original.manifest_number}
      </Link>
    ),
  },
  {
    id: 'vendor',
    header: 'Vendor',
    meta: { cellClass: 'text-on-surface' },
    cell: ({ row }) =>
      row.original.vendor_name || <span className="text-on-surface-variant">Not set</span>,
  },
  {
    id: 'line_count',
    header: 'Boards',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
    cell: ({ row }) => row.original.line_count,
  },
  {
    id: 'rma_number',
    header: 'Their RMA',
    // The vendor's own number, so it is an identifier too.
    meta: { cellClass: 'text-on-surface-variant font-mono whitespace-nowrap' },
    cell: ({ row }) => row.original.rma_number || '—',
  },
  {
    id: 'created_at',
    header: 'Started',
    meta: { cellClass: 'text-on-surface-variant whitespace-nowrap' },
    cell: ({ row }) => formatDate(row.original.created_at),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Tag tone={STATUS_META[row.original.status].tone}>{STATUS_META[row.original.status].label}</Tag>
    ),
  },
];

const BoardReturnsPage: React.FC = () => {
  const brApi = useBoardReturnsApi();
  const toast = useToast();
  const history = useHistory();
  const user = useUserStore((s) => s.user);

  // Mirrors requireCapability('procurement', 'manufacturing') on the router.
  const canAct = !!(
    user?.isadmin || user?.can_admin || user?.can_procurement || user?.can_manufacturing
  );

  const [rows, setRows] = useState<ManifestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'' | ManifestStatus>('');
  const [creating, setCreating] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await brApi.listManifests(statusFilter || undefined);
      setRows(data.rows || []);
    } catch (err: any) {
      toast.error(err?.message || 'Could not load vendor RMAs');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  /**
   * Start a manifest with nothing filled in and go straight to it.
   *
   * No vendor prompt first: the tech is holding a board and often does not know
   * or care which vendor it came from until later. The vendor is required to SEND,
   * which is where the question actually has to be answered.
   */
  const startManifest = async () => {
    setCreating(true);
    try {
      const { manifest } = await brApi.createManifest({});
      history.push(`/procurement/board-returns/${manifest.id}`);
    } catch (err: any) {
      toast.error(err?.message || 'Could not start a manifest');
    } finally {
      setCreating(false);
    }
  };

  if (!canAct) {
    // The header alone, with no action and no help: the page's own title is
    // what tells somebody they are in the right place and simply cannot get in.
    return (
      <PageLayout kind="focus" className="pt-6">
        <PageHeader
          title="Vendor RMA"
          lede="This needs procurement or manufacturing access. Ask an admin to add it."
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout kind="workspace" className="pt-6 space-y-5">
      <PageHeader
        title="Vendor RMA"
        helpTitle="Vendor RMA"
        lede="Failed boards going back to the vendor who sold them, with a reason on every one."
        help={
          <>
            <p>
              Failed boards going back to the vendor who sold them, one row per physical board.
              Each manifest is one shipment to one vendor, and prints as a packing slip plus a
              label for every board in the box.
            </p>
            <p>
              <b className="text-on-surface">Not the same as Returns.</b> That page tracks a
              quantity of a stocked part and the credit owed for it. This one tracks named units
              and why each one failed, including boards bought before the receiving hub existed
              that have no purchase order behind them at all.
            </p>
            <p>
              <b className="text-on-surface">Drafts are shared.</b> Start one at the flash station
              on a phone, scan boards into it, and pick it up on a desktop. Nothing needs saving.
            </p>
          </>
        }
        primary={
          /* The page's one consequential act, and the reason a tech walks over
             holding a tray. Everything else here is in service of finding a
             draft they already started. */
          <PrimaryAction onClick={startManifest} disabled={creating}>
            {creating ? 'Starting' : 'Start a manifest'}
          </PrimaryAction>
        }
      />

      <FilterBar>
        {/* The options are bare statuses, so the axis has to be said: "Draft"
            names a value, not what it narrows. */}
        <FilterSelect
          label="Show"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as '' | ManifestStatus)}
        >
          <option value="">In play</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="closed">Closed</option>
          <option value="cancelled">Cancelled</option>
        </FilterSelect>
      </FilterBar>

      {/* The vessel is the shared table's: shell, radius, scroll container and
          the face on every row. The status filter above stays page-local, and
          stays server-side — it re-fetches rather than filtering the array. */}
      <DataTable<ManifestRow>
        data={rows}
        columns={MANIFEST_COLUMNS}
        getRowId={(r) => r.id}
        loading={loading}
        loadingContent={
          <SkeletonGroup label="Loading manifests" className="space-y-1.5 p-4">
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </SkeletonGroup>
        }
        empty={
          <EmptyState
            variant="area"
            title={statusFilter ? 'No manifests with that status.' : 'No vendor RMAs in play.'}
          >
            Start one when you have failed boards to send back.
          </EmptyState>
        }
      />
    </PageLayout>
  );
};

export default BoardReturnsPage;
